import type { ReactNode } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getDatePickerLocaleText } from './datePickerLocaleText';

// Import both Serbian locales for dayjs (for month/day names)
import 'dayjs/locale/sr';
import 'dayjs/locale/sr-cyrl';

interface DatePickerLocaleProviderProps {
  children: ReactNode;
}

/**
 * Maps our app language codes to dayjs locale codes.
 *
 * App languages: 'sr-Latn' | 'sr-Cyrl'
 * Dayjs locales: 'sr' (Latin) | 'sr-cyrl' (Cyrillic)
 */
const getDayjsLocale = (appLanguage: string): string => {
  if (appLanguage === 'sr-Cyrl') {
    return 'sr-cyrl';
  }
  // Default to Serbian Latin
  return 'sr';
};

/**
 * Provides MUI X DatePicker localization that automatically syncs
 * with the app's language selection (Latin/Cyrillic).
 *
 * This provider handles two types of localization:
 * 1. adapterLocale (dayjs) - For month/day names (Januar, Понедељак, etc.)
 * 2. localeText - For UI text (Cancel/Откажи, OK/У реду, etc.)
 *
 * When user switches language via the header selector, both the calendar
 * content and UI text will automatically update to the correct script.
 */
export const DatePickerLocaleProvider = ({ children }: DatePickerLocaleProviderProps) => {
  const { i18n } = useTranslation();

  // Map app language to dayjs locale (for month/day names)
  const dayjsLocale = getDayjsLocale(i18n.language);

  // Set dayjs global locale so all dayjs.format() calls (e.g. dddd for day names)
  // use the correct Serbian script, not just MUI DatePicker components
  dayjs.locale(dayjsLocale);

  // Get Serbian locale text for UI elements (Cancel, OK, etc.)
  const localeText = getDatePickerLocaleText(i18n.language);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={dayjsLocale}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
};

